import React, { useState } from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import LoadingState from '@/components/LoadingState';
import Stack from '@/components/Stack';
import TextInput from '@/components/TextInput';
import { Body, Heading, Small } from '@/components/Typography';
import { axiosRubyClient } from '@/util/axios/axiosClient';

interface AnalysisParams {
  worker_id: string;
  skillId: string;
  taxType: string;
  backgroundCheckRequired: string;
  workerType: string;
  customerId: string;
  addressId: string;
  search: string;
  tab: string;
}

interface ExclusionResult {
  worker_id: number;
  worker_name: string;
  excluded: boolean;
  exclusion_reasons: string[];
  analysis_params: AnalysisParams;
}

interface ApiResponse {
  data: ExclusionResult;
}

const WorkerExclusionAnalyzer: React.FC = () => {
  const [params, setParams] = useState<AnalysisParams>({
    worker_id: '',
    skillId: '',
    taxType: 'w2',
    backgroundCheckRequired: 'true',
    workerType: 'lta',
    customerId: '',
    addressId: '',
    search: '',
    tab: 'all',
  });

  const [result, setResult] = useState<ExclusionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: keyof AnalysisParams, value: string) => {
    setParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAnalyze = async () => {
    if (!params.worker_id) {
      setError('Worker ID is required');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });

      const response = await axiosRubyClient.get<ApiResponse>(
        `/api/workers/analyze_exclusion?${queryParams.toString()}`,
      );

      setResult(response.data.data);
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || 'An error occurred',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Stack vertical gap={24}>
        <Heading>Worker Exclusion Analyzer</Heading>
        <Small>
          This utility analyzes why a worker might be excluded from shift
          assignments based on various criteria.
        </Small>

        <Card>
          <Card.Section>
            <Stack vertical gap={16}>
              <Heading>Analysis Parameters</Heading>

              <Stack gap={16} wrap>
                <div style={{ minWidth: '200px', flex: 1 }}>
                  <FormElement label="Worker ID *" htmlFor="worker_id" required>
                    <TextInput
                      id="worker_id"
                      name="worker_id"
                      value={params.worker_id}
                      onChange={(e) =>
                        handleInputChange('worker_id', e.target.value)
                      }
                      placeholder="555"
                    />
                  </FormElement>
                </div>

                <div style={{ minWidth: '200px', flex: 1 }}>
                  <FormElement label="Skill ID" htmlFor="skillId">
                    <TextInput
                      id="skillId"
                      name="skillId"
                      value={params.skillId}
                      onChange={(e) =>
                        handleInputChange('skillId', e.target.value)
                      }
                      placeholder="303"
                    />
                  </FormElement>
                </div>

                <div style={{ minWidth: '200px', flex: 1 }}>
                  <FormElement label="Customer ID" htmlFor="customerId">
                    <TextInput
                      id="customerId"
                      name="customerId"
                      value={params.customerId}
                      onChange={(e) =>
                        handleInputChange('customerId', e.target.value)
                      }
                      placeholder="456"
                    />
                  </FormElement>
                </div>

                <div style={{ minWidth: '200px', flex: 1 }}>
                  <FormElement label="Address ID" htmlFor="addressId">
                    <TextInput
                      id="addressId"
                      name="addressId"
                      value={params.addressId}
                      onChange={(e) =>
                        handleInputChange('addressId', e.target.value)
                      }
                      placeholder="789"
                    />
                  </FormElement>
                </div>
              </Stack>

              <Stack gap={16} wrap>
                <div style={{ minWidth: '200px', flex: 1 }}>
                  <FormElement label="Tax Type" htmlFor="taxType">
                    <TextInput
                      id="taxType"
                      name="taxType"
                      value={params.taxType}
                      onChange={(e) =>
                        handleInputChange('taxType', e.target.value)
                      }
                      placeholder="w2"
                    />
                  </FormElement>
                </div>

                <div style={{ minWidth: '200px', flex: 1 }}>
                  <FormElement
                    label="Background Check Required"
                    htmlFor="backgroundCheckRequired"
                  >
                    <TextInput
                      id="backgroundCheckRequired"
                      name="backgroundCheckRequired"
                      value={params.backgroundCheckRequired}
                      onChange={(e) =>
                        handleInputChange(
                          'backgroundCheckRequired',
                          e.target.value,
                        )
                      }
                      placeholder="true"
                    />
                  </FormElement>
                </div>

                <div style={{ minWidth: '200px', flex: 1 }}>
                  <FormElement label="Worker Type" htmlFor="workerType">
                    <TextInput
                      id="workerType"
                      name="workerType"
                      value={params.workerType}
                      onChange={(e) =>
                        handleInputChange('workerType', e.target.value)
                      }
                      placeholder="lta"
                    />
                  </FormElement>
                </div>

                <div style={{ minWidth: '200px', flex: 1 }}>
                  <FormElement label="Search" htmlFor="search">
                    <TextInput
                      id="search"
                      name="search"
                      value={params.search}
                      onChange={(e) =>
                        handleInputChange('search', e.target.value)
                      }
                      placeholder="david"
                    />
                  </FormElement>
                </div>
              </Stack>

              <Stack gap={16}>
                <div style={{ minWidth: '200px', flex: 1 }}>
                  <FormElement label="Tab" htmlFor="tab">
                    <TextInput
                      id="tab"
                      name="tab"
                      value={params.tab}
                      onChange={(e) => handleInputChange('tab', e.target.value)}
                      placeholder="all"
                    />
                  </FormElement>
                </div>
              </Stack>

              <div>
                <Button
                  a11yLabel="Analyze worker exclusion"
                  label="Analyze"
                  onClick={handleAnalyze}
                  disabled={loading || !params.worker_id}
                />
              </div>
            </Stack>
          </Card.Section>
        </Card>

        {loading && (
          <Card>
            <Card.Section>
              <Stack
                align="center"
                justify="center"
                style={{ padding: '40px' }}
              >
                <LoadingState overlayColor="transparent" />
                <Body>Analyzing worker exclusion...</Body>
              </Stack>
            </Card.Section>
          </Card>
        )}

        {error && (
          <Card>
            <Card.Section>
              <Stack vertical gap={8}>
                <Heading color="danger">Error</Heading>
                <Body color="danger">{error}</Body>
              </Stack>
            </Card.Section>
          </Card>
        )}

        {result && (
          <Card>
            <Card.Section>
              <Stack vertical gap={16}>
                <Heading>Analysis Results</Heading>

                <Stack vertical gap={8}>
                  <Body>
                    <strong>Worker ID:</strong> {result.worker_id}
                  </Body>
                  <Body>
                    <strong>Worker Name:</strong> {result.worker_name}
                  </Body>
                  <Body>
                    <strong>Excluded:</strong>{' '}
                    <span
                      style={{
                        color: result.excluded ? '#d32f2f' : '#2e7d32',
                        fontWeight: 'bold',
                      }}
                    >
                      {result.excluded ? 'YES' : 'NO'}
                    </span>
                  </Body>
                </Stack>

                {result.excluded && result.exclusion_reasons.length > 0 && (
                  <div>
                    <Body>
                      <strong>Exclusion Reasons:</strong>
                    </Body>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {result.exclusion_reasons.map((reason, index) => (
                        <li key={index}>
                          <Small color="danger">{reason}</Small>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <details style={{ marginTop: '16px' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                    Analysis Parameters Used
                  </summary>
                  <div
                    style={{
                      marginTop: '8px',
                      padding: '12px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px',
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: '12px' }}>
                      {JSON.stringify(result.analysis_params, null, 2)}
                    </pre>
                  </div>
                </details>
              </Stack>
            </Card.Section>
          </Card>
        )}
      </Stack>
    </div>
  );
};

export default WorkerExclusionAnalyzer;
